import React from 'react'
import { fromJSX, Widget } from 'widgets-for-react'
import { any, async, Updater } from 'widgets-for-react'
import { FoldersFiltering, FoldersLayout, FolderProps } from './foldersLayout'
import { FolderItem, Folders, FoldersState, FoldersStateUpdaters } from './foldersState'
import { GenericPageHeader } from '../sharedLayout/pageheader'
import { Alphabetized } from '../azNavigation/azNavigationUtils'
import { navigateWithParams } from '../../utils/router_utils'
import i18next from 'i18next'

const getAfdeling = (folder: FolderItem): string =>
  folder.departments[0] || folder.centrums[0] || ''

const enrichWithTracking = (filtered: Alphabetized<FolderItem>): Alphabetized<FolderProps> => {
  let index = 0
  return filtered.map((group) => ({
    ...group,
    items: group.items.map((folder) => {
      index++
      return {
        kind: folder.kind,
        title: folder.title,
        introduction: folder.introduction,
        url: folder.url,
        afdeling: getAfdeling(folder),
        index: index,
      }
    }),
  }))
}

const countResults = (folders: Alphabetized<FolderProps>): number =>
  folders.reduce((sum, group) => sum + group.items.length, 0)

export const foldersWidget = (state: FoldersState): Widget<Updater<FoldersState>> =>
  any([
    async<Folders>(state.folders).map(FoldersStateUpdaters.updateFoldersResultState),
    fromJSX((setState) => {
      if (state.folders.kind !== 'loaded') return <></>

      const filtered = performFiltering(state.folders.value, {
        searchTerm: state.folderFiltering.searchValue,
        departmentOrCentrum: state.folderFiltering.departmentOrCentrum,
      })
      const enrichedFolders = enrichWithTracking(filtered)
      const resultCount = countResults(enrichedFolders)

      return (
        <>
          <GenericPageHeader
            kind="folders"
            breadcrumbs={[
              { title: 'Spaarne Gasthuis', url: '/' },
              { title: 'Folders', url: '/folders' },
            ]}
            title={`${i18next.t('misc:folders_plural')}${state.folderFiltering.departmentOrCentrum ? ` - ${state.folderFiltering.departmentOrCentrum}` : ''}`}
          />
          <FoldersFiltering
            search={{
              searchInput: state.folderFiltering.searchValue,
              changeSearchInput: (newValue) => {
                updateQueryParams(newValue, state.folderFiltering.departmentOrCentrum)
                setState((oldState) => ({
                  ...oldState,
                  folderFiltering: {
                    ...oldState.folderFiltering,
                    searchValue: newValue,
                  },
                }))
              },
            }}
            filter={{
              selectedFilter: state.folderFiltering.departmentOrCentrum,
              changeSelectedFilter: (newValue) => {
                updateQueryParams(state.folderFiltering.searchValue, newValue)
                setState((oldState) => ({
                  ...oldState,
                  folderFiltering: {
                    ...oldState.folderFiltering,
                    departmentOrCentrum: newValue,
                  },
                }))
              },
            }}
            departmentOrCentrumList={concatDepartmentAndCentrums(state.folders.value)}
            resultCount={resultCount}
          />
          <FoldersLayout folders={enrichedFolders} />
        </>
      )
    }),
  ])

const performFiltering = (folders: Folders, filters: { searchTerm?: string; departmentOrCentrum?: string }) => {
  const normalizedSearchTerm = filters.searchTerm?.toLowerCase().trim()
  const normalizedDepartmentOrCentrum = filters.departmentOrCentrum?.toLowerCase().trim()

  const matchesDepartmentOrCentrum = (folder: FolderItem) => {
    const fields = [folder.departments, folder.subdepartments, folder.centrums, folder.subcentrums]
    return fields.some((field) => field.some((value) => value.toLowerCase().trim() === normalizedDepartmentOrCentrum))
  }

  return folders
    .map((letter) => ({
      ...letter,
      items: letter.items.filter((folder) => {
        const matchesSearchTerm = normalizedSearchTerm
          ? folder.title.toLowerCase().includes(normalizedSearchTerm)
          : true
        const matchesDeptOrCentrum = normalizedDepartmentOrCentrum ? matchesDepartmentOrCentrum(folder) : true
        return matchesSearchTerm && matchesDeptOrCentrum
      }),
    }))
    .filter((letter) => letter.items.length > 0)
}

const concatDepartmentAndCentrums = (folders: Folders) => {
  const departmentOrCentrumSet = new Set<string>()

  folders.forEach((folder) => {
    folder.items.forEach((item) => {
      item.departments.forEach((value) => departmentOrCentrumSet.add(value))
      item.centrums.forEach((value) => departmentOrCentrumSet.add(value))
    })
  })

  return [...departmentOrCentrumSet].sort((a, b) => a.localeCompare(b, 'nl'))
}

const updateQueryParams = (searchTerm?: string, departmentOrCentrum?: string) => {
  navigateWithParams('/folders', {
    afdelingOfCentrum: departmentOrCentrum?.toLowerCase().trim(),
    zoek: searchTerm?.toLowerCase().trim(),
  })
}
